<template>
    <aside class="direction">
        <button
            class="direction__option"
            :class="getStateClass('topLeft')"
            @mousedown="onMouseDown('topLeft')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon class="direction__top-left" :icon="['fas', 'arrow-alt-left']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('top')"
            @mousedown="onMouseDown('top')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon :icon="['fas', 'arrow-alt-up']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('topRight')"
            @mousedown="onMouseDown('topRight')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon class="direction__top-right" :icon="['fas', 'arrow-alt-right']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('left')"
            @mousedown="onMouseDown('left')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon :icon="['fas', 'arrow-alt-left']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('middle')"
            :style="middleButtonStyle"
            @mousedown="onMouseDown('middle')"
            @mouseup="onMouseUp"
        >
            <div v-if="!pressEnabled" class="direction__icon">
                <font-awesome-icon class="direction__middle" :icon="['fas', 'circle']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('right')"
            @mousedown="onMouseDown('right')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon :icon="['fas', 'arrow-alt-right']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('bottomLeft')"
            @mousedown="onMouseDown('bottomLeft')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon class="direction__bottom-left" :icon="['fas', 'arrow-alt-left']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('bottom')"
            @mousedown="onMouseDown('bottom')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon :icon="['fas', 'arrow-alt-down']" />
            </div>
        </button>

        <button
            class="direction__option"
            :class="getStateClass('bottomRight')"
            @mousedown="onMouseDown('bottomRight')"
            @mouseup="onMouseUp"
        >
            <div class="direction__icon">
                <font-awesome-icon class="direction__bottom-right" :icon="['fas', 'arrow-alt-right']" />
            </div>
        </button>
    </aside>
</template>

<script>
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'directional-pad',

    props: {
        value: {
            type: String,
            default: '',
        },

        // set to true to enable a "press and hold" mode
        // ... where on mouse down the input event will continue to emit, faster and faster, until mouse up
        pressEnabled: {
            type: Boolean,
            default: false,
        },

        // speed = number of press events that get fired per second
        speed: {
            type: Number,
            default: 250,
        },
    },

    data() {
        return {
            startTimeoutId: 0,
            loopTimeoutId: 0,
            pressed: true,
        };
    },

    computed: {
        timeoutDelay() {
            return 1000 / this.speed;
        },

        middleButtonStyle() {
            return {
                cursor: this.pressEnabled ? 'default' : 'auto',
            };
        },
    },

    methods: {
        onMouseDown(valueNew) {
            this.$emit('input', valueNew);

            if (this.pressEnabled && valueNew !== 'middle') {
                if (!this.pressed) {
                    this.pressed = true;

                    // wait half a second before starting the loop
                    this.startTimeoutId = window.setTimeout(() => this.onMouseDown(valueNew), 500);
                } else {
                    this.loopTimeoutId = window.setTimeout(() => this.onMouseDown(valueNew), this.timeoutDelay);
                }
            }
        },

        onMouseUp() {
            if (this.pressEnabled) {
                this.pressed = false;

                window.clearTimeout(this.startTimeoutId);
                window.clearTimeout(this.loopTimeoutId);
            }
        },

        getStateClass(valueNew) {
            return {
                [STATE_CLASSES.SELECTED]: !this.pressEnabled && valueNew === this.value,
            };
        },
    },
};
</script>

<style lang="scss" scoped>
.direction {
    $this: &;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    border: 1px solid var(--form-inputBorderColor);
    border-radius: 5px;
    overflow: hidden;

    &__option {
        display: flex;
        justify-content: center;
        align-items: center;
        color: var(--app-color);
        width: 33.3334%;
        padding: 5px 10px;

        &:hover {
            cursor: pointer;
        }

        &:nth-of-type(3n+2),
        &:nth-of-type(3n+3) {
            border-left: 1px solid var(--form-inputBorderColor);
        }

        &:nth-of-type(n+4) {
            border-top: 1px solid var(--form-inputBorderColor);
        }

        &.#{ $is-selected },
        &:active {
            color: var(--form-buttonHoverColor);
            background-color: var(--form-buttonHoverBackgroundColor);
        }
    }

    &__top-left {
        transform: rotate(45deg);
    }

    &__top-right {
        transform: rotate(-45deg);
    }

    &__middle {
        position: relative;
        top: -1px;
        font-size: 0.75em;
    }

    &__bottom-left {
        transform: rotate(-45deg);
    }

    &__bottom-right {
        transform: rotate(45deg);
    }
}
</style>
